import React, { ReactElement, useState, useCallback } from 'react';
import { MutationFunctionOptions } from '@apollo/client';
import { ExecutionResult } from 'graphql';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import FaceIcon from '@material-ui/icons/Face';
import SaveIcon from '@material-ui/icons/Save';
import { useTranslation } from 'react-i18next';

import Fab from '../Fab';

interface AvatarWidgetProps {
  avatarUrl?: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  updateMe: (
    options?: MutationFunctionOptions<any, Record<string, any>> | undefined
  ) => Promise<ExecutionResult<any>>;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      border: 0
    },
    currentAvatarHolder: {
      padding: 0,
      display: 'flex',
      flexDirection: 'row'
    },
    currentAvatar: {
      width: theme.spacing(10),
      height: theme.spacing(10)
    },
    actionHolder: {
      marginLeft: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center'
    },
    actionCancel: {
      marginLeft: theme.spacing(1.5)
    },
    avatarPickerHolder: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'start'
    },
    avatarButton: {
      padding: 0,
      boxShadow: 'none',
      margin: theme.spacing(1.88)
    },
    avatarImages: {
      width: theme.spacing(7),
      height: theme.spacing(7)
    }
  })
);

export const AVATARS: string[] = [
  '/images/avatar_01.png',
  '/images/avatar_02.png',
  '/images/avatar_03.png',
  '/images/avatar_04.png',
  '/images/avatar_05.png',
  '/images/avatar_06.png',
  '/images/avatar_07.png',
  '/images/avatar_08.png',
  '/images/avatar_09.png',
  '/images/avatar_10.png',
  '/images/avatar_11.png',
  '/images/avatar_12.png'
];

const AvatarWidget = ({
  avatarUrl,
  firstName,
  lastName,
  updateMe
}: AvatarWidgetProps): ReactElement => {
  const currentAvatar = avatarUrl;
  const classes = useStyles();
  const { t } = useTranslation('AvatarWidget');
  const [expanded, setExpanded] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(currentAvatar);
  const userInitials = firstName && lastName ? firstName.charAt(0) + lastName.charAt(0) : '';

  const handleExpandClick = (): void => {
    setExpanded(true);
  };

  const handleCloseClick = (): void => {
    setSelectedAvatar(currentAvatar);
    setExpanded(false);
  };

  const handleSaveClick = async (): Promise<void> => {
    await updateMe({
      variables: {
        data: {
          avatarUrl: selectedAvatar
        }
      }
    });
    setExpanded(false);
  };

  const handleAvatarClick = useCallback((avatar: string) => (): void => setSelectedAvatar(avatar), [
    setSelectedAvatar
  ]);

  return (
    <Paper className={classes.root} elevation={0}>
      <CardContent className={classes.currentAvatarHolder}>
        <Avatar
          alt={`${firstName} ${lastName}`}
          className={classes.currentAvatar}
          src={selectedAvatar || '' /* TODO: fallback? */}
        >
          {userInitials}
        </Avatar>
        <div className={classes.actionHolder}>
          {expanded ? (
            <div>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                disableElevation
                startIcon={<SaveIcon />}
                onClick={handleSaveClick}
              >
                {t('Save Avatar')}
              </Button>
              <Button
                variant="text"
                size="small"
                disableElevation
                onClick={handleCloseClick}
                className={classes.actionCancel}
              >
                {t('Cancel')}
              </Button>
            </div>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              disableElevation
              startIcon={<FaceIcon />}
              onClick={handleExpandClick}
            >
              {t('Change Avatar')}
            </Button>
          )}
        </div>
      </CardContent>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          <Typography variant="subtitle1">{t('Choose one of the following avatars:')}</Typography>
          <div className={classes.avatarPickerHolder}>
            {AVATARS.map((avatar, key) => (
              <Fab
                key={`avatar-option-${key}`}
                className={classes.avatarButton}
                variant="text"
                size="medium"
                onClick={handleAvatarClick(avatar)}
                startIcon={
                  <Avatar
                    alt={`avatar-option-${key}`}
                    className={classes.avatarImages}
                    src={avatar}
                  />
                }
              >
                <span />
              </Fab>
            ))}
          </div>
        </CardContent>
      </Collapse>
    </Paper>
  );
};

export default AvatarWidget;
